import React, { memo } from 'react';
import { Box, Flex, Heading, Image, Text, Button, SimpleGrid, Link } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);

const categories = [
  {
    id: 1,
    name: "Rings",
    description: "Elegant rings for every occasion",
    image: "/rings-category.webp",
    link: "/products/rings"
  },
  {
    id: 2,
    name: "Bracelets",
    description: "Stylish bracelets to adorn your wrist",
    image: "/bracelets-category.webp",
    link: "/products/bracelets"
  },
  {
    id: 3,
    name: "Pendants",
    description: "Beautiful pendants to complement your style",
    image: "/pendants-category.webp",
    link: "/products/pendants"
  },
  {
    id: 4,
    name: "Chains",
    description: "Exquisite chains for the perfect finishing touch",
    image: "/chains-category.webp",
    link: "/products/chains"
  }
];

const ProductCategories = memo(() => {
  return (
    <Box py={{ base: 12, md: 16 }} px={{ base: 4, md: 8, lg: 16 }} id="product-categories">
      <MotionHeading 
        as="h2" 
        textAlign="center" 
        fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }} 
        mb={{ base: 8, md: 12 }}
        color="gray.800"
        fontFamily="'Cormorant', serif"
        fontWeight="700"
        letterSpacing="wide"
        initial={{ opacity: 0, y: -50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
      >
        Our Jewelry Collections
      </MotionHeading>
      
      <SimpleGrid 
        columns={{ base: 1, md: 2, lg: 4 }} 
        spacing={{ base: 6, md: 6, lg: 8 }}
        maxW={{ base: "100%", md: "1200px", lg: "1400px" }}
        mx="auto"
      >
        {categories.map((category, index) => (
          <MotionBox
            as={RouterLink}
            to={category.link}
            key={category.id}
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3,delay: index * 0.1, duration: 0.5 }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <Box
              bg="white"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="md"
              transition="all 0.3s"
              _hover={{ boxShadow: "xl", textDecoration: "none" }}
              height="100%"
              display="flex"
              flexDirection="column"
            >
              <Box position="relative" overflow="hidden" h={{ base: "180px", md: "220px", lg: "300px" }}>
                <Image
                  src={category.image}
                  alt={category.name}
                  objectFit="cover"
                  w="100%"
                  h="100%"
                  transition="transform 0.5s"
                  _hover={{ transform: "scale(1.1)" }}
                />
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  w="100%"
                  h="100%"
                  bg="linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.1) 60%)"
                />
              </Box>
              
              <Flex 
                p={{ base: 4, md: 5, lg: 8 }}
                flexDirection="column" 
                flex="1"
                justifyContent="space-between"
              >
                <Box>
                  <MotionHeading 
                    as="h3" 
                    fontSize={{ base: "xl", md: "xl", lg: "2xl" }}
                    mb={{ base: 2, lg: 3 }}
                    color="gray.800"
                    fontFamily="'Cormorant', serif"
                    fontWeight="700"
                    letterSpacing="wide"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                  >
                    {category.name}
                  </MotionHeading>
                  <MotionText 
                    color="gray.600" 
                    mb={{ base: 3, lg: 4 }}
                    fontSize={{ base: "sm", md: "sm", lg: "md" }}
                    fontFamily="'Zain', sans-serif"
                    fontWeight="500"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 + 0.4, duration: 0.5 }}
                  >
                    {category.description}
                  </MotionText>
                </Box>
                
                <Link as={RouterLink} to={category.link} _hover={{ textDecoration: "none" }}>
                  <MotionButton
                    colorScheme="gray"
                    size={{ base: "sm", md: "sm", lg: "md" }}
                    width="100%"
                    variant="outline"
                    borderWidth="2px"
                    borderColor="black"
                    color="black"
                    fontFamily="'Zain', sans-serif"
                    fontWeight="600"
                    _hover={{ bg: "black", color: "white", borderColor: "black" }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
                  >
                    Shop Now
                  </MotionButton>
                </Link>
              </Flex>
            </Box>
          </MotionBox>
        ))}
      </SimpleGrid>
    </Box>
  );
});

export default ProductCategories;